/**
 * Shared settings-hub actions used by the lobby settings screen and the
 * in-game settings modal. Keep the destinations here so both entry points
 * always offer the same controls.
 */

import React from 'react'
import { Alert, Platform, Text, TouchableOpacity, View } from 'react-native'
import { useRouter } from 'expo-router'
import { useAuth } from '../../hooks/useAuth'
import { useSupportUnread } from '../../hooks/useSupportInbox'
import { useGames } from '../../hooks/useGames'
import { isCompleteGame } from '../../lib/lobbyTabs'
import { HubCard, type HubIconName } from './HubCard'
import { useTheme } from '../../hooks/useTheme'
import { QaThemeToggle } from './ThemeToggle'
import { APP_VERSION } from '../../lib/appVersion'
import { WebUpdateRow } from '../ui/WebUpdateBadge'

type SettingsRoute =
  | '/(app)/settings/account'
  | '/(app)/settings/help'
  | '/(app)/tutorial'
  | '/(app)/settings/invite'
  | '/(app)/settings/about'

const SETTINGS_ACTIONS: ReadonlyArray<{
  icon: HubIconName
  title: string
  subtitle: string
  route: SettingsRoute
  accessibilityLabel: string
}> = [
  {
    icon: 'cog',
    title: 'General settings',
    subtitle: 'Account, notifications, and preferences',
    route: '/(app)/settings/account',
    accessibilityLabel: 'General settings',
  },
  {
    icon: 'school',
    title: 'Tutorial',
    subtitle: 'Learn the basics or start a guided practice game',
    route: '/(app)/tutorial',
    accessibilityLabel: 'Tutorial',
  },
  {
    icon: 'lifebuoy',
    title: 'Help & Support',
    subtitle: 'Bugs • Feature Requests • Report User',
    route: '/(app)/settings/help',
    accessibilityLabel: 'Help & Support',
  },
  {
    icon: 'email-outline',
    title: 'Invite a Friend',
    subtitle: 'Send an email invite to play WarCarp',
    route: '/(app)/settings/invite',
    accessibilityLabel: 'Invite a friend to WarCarp',
  },
  {
    icon: 'information-outline',
    title: 'About WarCarp',
    subtitle: 'Version, rules, and contact',
    route: '/(app)/settings/about',
    accessibilityLabel: 'About WarCarp',
  },
]

interface SettingsHubContentProps {
  /** Tutorial is offered only from top-level settings, never during a game. */
  showTutorial?: boolean
  /** In-game menus offer the QA theme shortcut directly instead. */
  showQaTheme?: boolean
  /** Lets a modal remove its history layer before the destination is pushed. */
  navigateAfterDismiss?: (navigate: () => void) => void
}

export function SettingsHubContent({
  navigateAfterDismiss,
  showTutorial = true,
  showQaTheme = true,
}: SettingsHubContentProps) {
  const { logout } = useAuth()
  const unread = useSupportUnread().data?.count ?? 0
  const { data: games } = useGames()
  // Experience counts completed games of any classification, not ranked stats.
  const completedGames = games?.filter(isCompleteGame).length ?? 0
  const router = useRouter()
  const { theme } = useTheme()
  const iconColor = theme === 'dark' ? '#e6e1c4' : '#18170f'
  const actions = SETTINGS_ACTIONS.filter((action) =>
    !(action.route === '/(app)/tutorial' && (!showTutorial || completedGames >= 3)),
  )

  function afterDismiss(callback: () => void) {
    if (navigateAfterDismiss) {
      navigateAfterDismiss(callback)
      return
    }
    callback()
  }

  function navigate(route: SettingsRoute) {
    afterDismiss(() => router.push(route))
  }

  async function completeSignOut() {
    await logout()
    router.replace('/(auth)/login')
  }

  function signOut() {
    afterDismiss(() => void completeSignOut())
  }

  function handleSignOut() {
    if (Platform.OS === 'web') {
      if (window.confirm('Sign out?')) signOut()
      return
    }

    Alert.alert('Sign Out', 'Are you sure?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Sign Out', style: 'destructive', onPress: signOut },
    ])
  }

  return (
    <View className="gap-4">
      <WebUpdateRow />
      {showQaTheme && <QaThemeToggle />}
      {actions.map((action) => (
        <HubCard
          key={action.route}
          icon={action.icon}
          iconColor={iconColor}
          title={action.title}
          subtitle={action.subtitle}
          onPress={() => navigate(action.route)}
          accessibilityLabel={action.accessibilityLabel}
          unreadCount={action.route === '/(app)/settings/help' ? unread : 0}
        />
      ))}

      <View className="h-px bg-olive" />
      <TouchableOpacity
        onPress={handleSignOut}
        accessibilityLabel="Sign out"
        accessibilityRole="button"
        className="bg-blood rounded-xl px-4 py-4 items-center"
        style={{ minHeight: 56 }}
      >
        <Text className="font-stencil text-white text-base uppercase">Sign Out</Text>
      </TouchableOpacity>
      <Text testID="settings-app-version" className="font-typed text-ink text-right"
        style={{ fontSize: 11, opacity: 0.7, marginTop: -8 }}>{APP_VERSION}</Text>
    </View>
  )
}
